import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Alert,
  Autocomplete,
  Chip,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { AuthContext } from "react-oauth2-code-pkce";
import OrgStore from "src/stores/OrgStore";
import { IUserWithRoles } from "src/models/user.types";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import CheckIcon from "@mui/icons-material/Check";

interface UserDetailsProps {
  emailAddress: string;
}

const InviteUserDetails: React.FC<UserDetailsProps> = ({ emailAddress }) => {
  const [userDetails, setUserDetails] = useState<IUserWithRoles>({
    id: emailAddress,
    idType: "email",
    roles: [],
  });
  const [roleToAdd, setRoleToAdd] = useState("");
  const { token } = useContext(AuthContext);
  const { t } = useTranslation();
  const [assignableRoles, setAssignableRoles] = useState<string[]>([]);
  const [dirty, setDirty] = useState<boolean>(false);
  const [alert, setAlert] = useState<string | undefined>(undefined);
  const [isErrorAlert, setErrorAlert] = useState<boolean>(false);

  useEffect(() => {
    const fetchDetails = async () => {
      const assignableRoles =
        OrgStore.currentOrganisation?.assignableRoles ?? [];
      setAssignableRoles(assignableRoles);
    };

    setAssignableRoles([]);

    if (emailAddress && token) {
      fetchDetails().catch((e) => {
        setErrorAlert(true);
        setAlert(t("result.error", { errorMessage: (e as Error).message }));
      });
    }
  }, [emailAddress, token]);

  const handleTextFieldChange = (event) => {
    setRoleToAdd(event.target.textContent);
  };

  const handleDelete = useCallback(
    (roleToDelete: string) => {
      // console.log("Delete role: " + roleToDelete);
      if (userDetails) {
        const updatedRoles = userDetails.roles.filter(
          (role) => role !== roleToDelete,
        );
        setUserDetails({
          ...userDetails,
          roles: updatedRoles,
        });
        const orgAssignableRoles =
          OrgStore.currentOrganisation?.assignableRoles ?? [];
        if (orgAssignableRoles.includes(roleToDelete))
          assignableRoles.push(roleToDelete);
        setAssignableRoles(assignableRoles);
        setAlert(undefined);
        setDirty(true);
      }
    },
    [assignableRoles, userDetails],
  );

  const handleAdd = useCallback(() => {
    console.log("Add role to user: " + roleToAdd);
    if (roleToAdd == null || roleToAdd == "" || roleToAdd == undefined) {
      return;
    }
    if (
      userDetails &&
      userDetails.roles &&
      !userDetails.roles.includes(roleToAdd)
    ) {
      setUserDetails({
        ...userDetails,
        roles: [...userDetails.roles, roleToAdd],
      });
      const newAssignableRoles = assignableRoles.filter(
        (role) => role !== roleToAdd,
      );
      setAssignableRoles(newAssignableRoles);
    }
    setRoleToAdd("");
    setAlert(undefined);
    setDirty(true);
  }, [assignableRoles, userDetails, roleToAdd]);

  const handleSave = useCallback(async () => {
    if (userDetails != null && emailAddress) {
      try {
        await OrgStore.inviteUser(userDetails, token);
        setErrorAlert(false);
        setAlert(t("result.success"));
      } catch (e) {
        setErrorAlert(true);
        setAlert(t("result.error", { errorMessage: (e as Error).message }));
      }
    }
    setDirty(false);
  }, [userDetails, t, emailAddress]);

  return (
    <List>
      <ListItem>
        <Typography>{"Email: " + emailAddress}</Typography>
      </ListItem>

      <ListItem>
        <Typography>{t("org.roles") + ":"}</Typography>
      </ListItem>
      <ListItem>
        {userDetails && userDetails.roles.length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {userDetails.roles?.map((role) => (
              <Chip
                color={role === "ROLE_ORGANIZATION_ADMIN" ? "error" : "primary"}
                variant="outlined"
                key={role}
                label={role}
                onDelete={() => handleDelete(role)}
              />
            ))}
          </Stack>
        ) : (
          t("user.not_found")
        )}
      </ListItem>
      {assignableRoles && assignableRoles.length > 0 && (
        <ListItem>
          <Autocomplete
            disablePortal
            id="add_role"
            options={assignableRoles}
            sx={{ width: 300 }}
            value={roleToAdd}
            onChange={handleTextFieldChange}
            renderInput={(params) => (
              <TextField {...params} label={t("user.add_role")} />
            )}
          />
          <IconButton onClick={handleAdd} color="primary">
            <AddIcon />
          </IconButton>
        </ListItem>
      )}
      <ListItem>
        <Stack direction="row" spacing={1}>
          <Button disabled={!dirty} onClick={handleSave} color="primary">
            {t("user.invite")}
          </Button>
        </Stack>
      </ListItem>
      {alert && (
        <ListItem>
          <Alert
            icon={
              isErrorAlert ? (
                <ErrorIcon fontSize="inherit" />
              ) : (
                <CheckIcon fontSize="inherit" />
              )
            }
            severity={isErrorAlert ? "error" : "success"}
          >
            {alert}
          </Alert>
        </ListItem>
      )}
    </List>
  );
};

export default InviteUserDetails;
